import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { physioApi, categoryApi, getAuthSession } from '../services/api';
import SlotAvailabilityPicker from '../components/SlotAvailabilityPicker';
import '../styles/TherapistsDirectory.css';

function TherapistsPage() {
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = useState('');
  const [activeCategory, setActiveCategory] = useState('All');
  const [selectedConcern, setSelectedConcern] = useState('All Concerns');
  const [membershipFilter, setMembershipFilter] = useState('all'); // 'all', 'pro', 'free'
  const [practitioners, setPractitioners] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedTherapist, setSelectedTherapist] = useState(null);

  const handleViewDetails = (therapist, e) => {
    if (e) e.preventDefault();
    const tid = therapist._id || therapist.id;
    navigate(`/therapist/${tid}`);
  };

  const handleBookSession = (therapist, e, slot = null) => {
    if (e) e.preventDefault();
    const tid = therapist._id || therapist.id;
    let targetUrl = `/therapist/${tid}?action=book`;

    if (slot) {
      targetUrl += `&date=${slot.date}&time=${slot.startTime}&slotId=${slot._id}&mode=${slot.mode || 'online'}`;
    }

    navigate(targetUrl);
  };

  useEffect(() => {
    const fetchPractitioners = async () => {
      try {
        setLoading(true);
        const data = await physioApi.search();
        const list = data.profiles || data.therapists || data.users || [];

        const teamImages = [
          '/assets/images/team/team-1-1.jpg',
          '/assets/images/team/team-1-2.jpg',
          '/assets/images/team/team-1-3.jpg',
          '/assets/images/team/team-1-4.jpg',
          '/assets/images/team/team-1-5.jpg',
          '/assets/images/team/team-1-6.jpg',
          '/assets/images/team/team-2-1.jpg',
          '/assets/images/team/team-2-2.jpg',
        ];

        const defaultConcerns = [
          ['Anxiety & Panic', 'Depression', 'CBT Therapy', 'Workplace Stress'],
          ['Relationship Issues', 'Couples Counseling', 'Self-Esteem', 'Life Transitions'],
          ['Trauma & PTSD', 'Grief & Loss', 'Mindfulness', 'Emotional Regulation'],
          ['Child & Adolescent Care', 'ADHD & Focus', 'Behavioral Therapy', 'Family Dynamics'],
          ['Stress Management', 'Burnout Recovery', 'Anger Management', 'Sleep Health']
        ];

        // Format 100% dynamic database practitioners
        const formattedFromDb = list.map((t, idx) => {
          const isPro = t.membership === 'pro';
          const expYears = Number(t.yearsOfExperience) || (isPro ? 8 : 4);
          const specialty = t.specialization || (isPro ? 'Senior Clinical Psychologist & Psychotherapist' : 'Licensed Counselor & Therapist');

          let concerns = [];
          if (t.specialization && t.specialization.includes(',')) {
            concerns = t.specialization.split(',').map(s => s.trim());
          } else if (t.specialization) {
            concerns = [t.specialization, 'General Counseling', 'Mental Wellness'];
          } else {
            concerns = defaultConcerns[idx % defaultConcerns.length];
          }

          return {
            _id: t._id || t.id,
            name: t.name ? (t.name.startsWith('Dr.') ? t.name : `Dr. ${t.name}`) : 'Licensed Mental Health Professional',
            rawName: t.name || 'Practitioner',
            specialty: specialty,
            qualification: isPro ? 'M.Phil Clinical Psychology (RCI Licensed), Ph.D.' : 'M.A. Applied Psychology, Registered Practitioner',
            experience: expYears,
            location: t.location || 'Mumbai, India (Online & In-Person)',
            languages: ['English', 'Hindi', idx % 2 === 0 ? 'Marathi' : 'Bengali'],
            concerns: concerns,
            bio: t.bio || `Specialized mental health professional with ${expYears}+ years of clinical experience. Providing evidence-based, compassionate psychotherapy and individualized treatment plans to help clients navigate anxiety, mood difficulties, and interpersonal relationships.`,
            approach: isPro
              ? 'Integrative Cognitive Behavioral Therapy (CBT), Acceptance and Commitment Therapy (ACT), and Trauma-Informed Mindfulness.'
              : 'Client-Centered Counseling, Solution-Focused Brief Therapy (SFBT), and Cognitive Behavioral Therapy.',
            image: t.image || teamImages[idx % teamImages.length],
            isVIP: isPro,
            membership: t.membership || (isPro ? 'pro' : 'free'),
            rating: (4.8 + (idx % 3) * 0.1).toFixed(1),
            reviewCount: 35 + (idx * 12) + (isPro ? 40 : 0),
            consultationFee: t.consultationFee || 999,
            consultationDuration: t.consultationDuration || 50,
            durationFees: t.durationFees || {
              30: Math.round((t.consultationFee || 999) * 0.65),
              50: (t.consultationFee || 999),
              80: Math.round((t.consultationFee || 999) * 1.5),
            },
            availableSlots: Array.isArray(t.availableSlots) ? t.availableSlots.filter(s => !s.isBooked) : [],
            weeklySchedule: t.weeklySchedule || null,
          };
        });

        // 🚀 SORTING: PRO MEMBERS FIRST (from Database), THEN FREE MEMBERS
        formattedFromDb.sort((a, b) => {
          if (a.isVIP && !b.isVIP) return -1;
          if (!a.isVIP && b.isVIP) return 1;
          return b.experience - a.experience;
        });

        setPractitioners(formattedFromDb);
      } catch (err) {
        console.error('Error fetching therapists from database:', err);
        setPractitioners([]);
      } finally {
        setLoading(false);
      }
    };
    fetchPractitioners();
  }, []);

  const [categories, setCategories] = useState([
    { name: 'All', icon: '🌟' },
    { name: 'Family Therapist', icon: '👨‍👩‍👧' },
    { name: 'Career Counseling', icon: '💼' },
    { name: 'Clinical Psychology', icon: '🧠' },
    { name: 'Child & Adolescent', icon: '👶' },
    { name: 'Couples & Marriage', icon: '💑' },
    { name: 'Trauma & PTSD', icon: '🌿' },
    { name: 'Stress & Burnout', icon: '🎯' },
  ]);

  useEffect(() => {
    const fetchCategories = async () => {
      try {
        const res = await categoryApi.getPublicCategories();
        if (res?.success && res.categories?.length > 0) {
          setCategories([
            { name: 'All', icon: '🌟' },
            ...res.categories.map(c => ({ name: c.name, icon: c.icon || '🌿' }))
          ]);
        }
      } catch (err) {
        console.error('Error fetching categories:', err);
      }
    };
    fetchCategories();
  }, []);

  const concernFilters = [
    'All Concerns',
    'Anxiety & Panic',
    'Depression',
    'Relationship Issues',
    'Stress & Burnout',
    'Trauma & PTSD',
    'Self-Esteem',
    'ADHD & Focus',
    'Grief & Loss'
  ];

  // Filtered practitioners list (100% Dynamic from Database)
  const filteredPractitioners = practitioners.filter(p => {
    // Search query match
    const searchLower = searchQuery.trim().toLowerCase();
    const matchesSearch =
      !searchLower ||
      p.name.toLowerCase().includes(searchLower) ||
      p.specialty.toLowerCase().includes(searchLower) ||
      p.location.toLowerCase().includes(searchLower) ||
      p.concerns.some(c => c.toLowerCase().includes(searchLower));

    // Category match
    const cleanCat = activeCategory.toLowerCase()
      .replace('therapist', '')
      .replace('therapy', '')
      .replace('counseling', '')
      .replace('&', '')
      .trim();

    const matchesCategory =
      !activeCategory ||
      activeCategory === 'All' ||
      p.specialty.toLowerCase().includes(cleanCat) ||
      p.concerns.some(c => c.toLowerCase().includes(cleanCat));

    // Concern pill match
    const matchesConcern =
      !selectedConcern ||
      selectedConcern === 'All' ||
      selectedConcern === 'All Concerns' ||
      p.concerns.some(c => c.toLowerCase().includes(selectedConcern.toLowerCase().replace('& panic', '').replace('& focus', '').replace('& burnout', '').replace('& ptsd', '').replace('& loss', '').trim()));

    // Membership tier filter (all, pro, free)
    const matchesMembership =
      !membershipFilter ||
      membershipFilter === 'all' ||
      (membershipFilter === 'pro' && p.isVIP) ||
      (membershipFilter === 'free' && !p.isVIP);

    return matchesSearch && matchesCategory && matchesConcern && matchesMembership;
  });

  const proCount = practitioners.filter(p => p.isVIP).length;
  const freeCount = practitioners.filter(p => !p.isVIP).length;

  return (
    <div className="therapists-page-wrapper">
      {/* Hero Header */}
      <section className="therapists-hero-section">
        <div className="container">
          <div className="row align-items-center">
            <div className="col-lg-8">
              <span style={{ color: '#a78bfa', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '2px', fontSize: '0.85rem' }}>
                Expert Care & Mental Wellness
              </span>
              <h1 className="therapists-hero-title">
                Find Your Ideal Mental Health Practitioner
              </h1>
              <p className="therapists-hero-subtitle">
                Connect with verified clinical psychologists, psychiatrists, and licensed therapists. Browse verified credentials, clinical focus areas, and book instant 1-on-1 sessions.
              </p>

              <div className="trust-badges-row">
                <div className="trust-badge-item">
                  <span>🛡️</span> 100% Verified Credentials
                </div>
                <div className="trust-badge-item">
                  <span>🔒</span> Confidential & Encrypted
                </div>
                <div className="trust-badge-item">
                  <span>💻</span> Online & In-Clinic
                </div>
                <div className="trust-badge-item">
                  <span>⭐</span> 4.9/5 Patient Rating
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Amaha-Style Category Navigation Tabs */}
      <div className="category-tabs-container">
        <div className="container">
          <div className="category-tabs-scroll">
            {categories.map((cat) => {
              const catName = typeof cat === 'object' ? cat.name : cat;
              const catIcon = typeof cat === 'object' ? cat.icon : '🌿';
              return (
                <button
                  key={catName}
                  className={`category-tab-btn ${activeCategory === catName ? 'active' : ''}`}
                  onClick={() => setActiveCategory(catName)}
                >
                  {catName === 'All' ? '🌟 All Specialists' : `${catIcon} ${catName}`}
                </button>
              );
            })}
          </div>
        </div>
      </div>

      {/* Main Directory Section */}
      <section style={{ padding: '20px 0 80px' }}>
        <div className="container">
          {/* Search and Filters Card */}
          <div className="filter-search-card">
            <div className="row align-items-center">
              <div className="col-lg-6 mb-3 mb-lg-0">
                <div className="search-input-wrapper">
                  <span className="search-icon-left">🔍</span>
                  <input
                    type="text"
                    className="search-input-field"
                    placeholder="Search by therapist name, clinical concern (e.g. Anxiety, Depression), or location..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                  />
                </div>
              </div>

              {/* Membership Tier Filter Buttons (Default: All with Pro first, or filter by Pro/Free) */}
              <div className="col-lg-6 d-flex justify-content-lg-end align-items-center gap-2 flex-wrap">
                <span style={{ fontSize: '0.82rem', fontWeight: 700, color: '#64748b', marginRight: '4px' }}>
                  Plan View:
                </span>
                <button
                  type="button"
                  onClick={() => setMembershipFilter('all')}
                  style={{
                    padding: '9px 15px',
                    borderRadius: '10px',
                    border: membershipFilter === 'all' ? '2px solid #7c3aed' : '1px solid #e2e8f0',
                    background: membershipFilter === 'all' ? '#7c3aed' : '#ffffff',
                    color: membershipFilter === 'all' ? '#ffffff' : '#475569',
                    fontSize: '0.85rem',
                    fontWeight: 700,
                    cursor: 'pointer',
                    transition: 'all 0.2s ease',
                  }}
                >
                  🌟 All (Pro & Free)
                </button>
                <button
                  type="button"
                  onClick={() => setMembershipFilter('pro')}
                  style={{
                    padding: '9px 15px',
                    borderRadius: '10px',
                    border: membershipFilter === 'pro' ? '2px solid #7c3aed' : '1px solid #e2e8f0',
                    background: membershipFilter === 'pro' ? '#7c3aed' : '#ffffff',
                    color: membershipFilter === 'pro' ? '#ffffff' : '#475569',
                    fontSize: '0.85rem',
                    fontWeight: 700,
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '4px',
                    transition: 'all 0.2s ease',
                  }}
                >
                  <span>💎</span> Pro ({proCount})
                </button>
                <button
                  type="button"
                  onClick={() => setMembershipFilter('free')}
                  style={{
                    padding: '9px 15px',
                    borderRadius: '10px',
                    border: membershipFilter === 'free' ? '2px solid #10b981' : '1px solid #e2e8f0',
                    background: membershipFilter === 'free' ? '#10b981' : '#ffffff',
                    color: membershipFilter === 'free' ? '#ffffff' : '#475569',
                    fontSize: '0.85rem',
                    fontWeight: 700,
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '4px',
                    transition: 'all 0.2s ease',
                  }}
                >
                  <span>🌱</span> Free Essential ({freeCount})
                </button>
              </div>
            </div>

            {/* Quick Concern Filter Pills */}
            <div className="concern-pills-row">
              <span style={{ fontSize: '0.8rem', fontWeight: 700, color: '#64748b', alignSelf: 'center', marginRight: '4px' }}>
                Filter by Concern:
              </span>
              {concernFilters.map((concern) => (
                <button
                  key={concern}
                  className={`concern-pill-btn ${selectedConcern === concern ? 'active' : ''}`}
                  onClick={() => setSelectedConcern(concern)}
                >
                  {concern}
                </button>
              ))}
            </div>
          </div>

          {/* Directory Count Header */}
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px', flexWrap: 'wrap', gap: '10px' }}>
            <h3 style={{ fontSize: '1.2rem', fontWeight: 700, color: '#1e293b', margin: 0 }}>
              Available Practitioners ({filteredPractitioners.length})
            </h3>
            <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
              <span style={{
                fontSize: '0.8rem',
                color: '#6d28d9',
                background: '#f5f3ff',
                padding: '4px 10px',
                borderRadius: '12px',
                fontWeight: 700,
                border: '1px solid #e9d5ff'
              }}>
                💎 {practitioners.filter(p => p.isVIP).length} Pro (Priority Listed)
              </span>
              <span style={{
                fontSize: '0.8rem',
                color: '#065f46',
                background: '#ecfdf5',
                padding: '4px 10px',
                borderRadius: '12px',
                fontWeight: 700,
                border: '1px solid #a7f3d0'
              }}>
                🌱 {practitioners.filter(p => !p.isVIP).length} Free Essential
              </span>
            </div>
          </div>

          {/* Practitioners Grid (100% Dynamic Database Records) */}
          {loading ? (
            <div style={{ textAlign: 'center', padding: '80px 0' }}>
              <div className="spinner-border text-primary" role="status" style={{ width: '3rem', height: '3rem', color: '#7c3aed' }}>
                <span className="visually-hidden">Loading practitioners...</span>
              </div>
              <p style={{ marginTop: '16px', color: '#64748b', fontWeight: 600 }}>Loading verified practitioners from database...</p>
            </div>
          ) : filteredPractitioners.length > 0 ? (
            <div className="row g-4">
              {filteredPractitioners.map((t, idx) => (
                <div className="col-lg-4 col-md-6" key={t._id || idx}>
                  <div className={`therapist-card ${t.isVIP ? 'is-pro-card' : ''}`}>
                    {/* Card Top Bar with Membership Badge & Status */}
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '14px' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '0.75rem', fontWeight: 700, color: '#059669' }}>
                        <span style={{ width: '8px', height: '8px', borderRadius: '50%', backgroundColor: '#10b981', display: 'inline-block' }}></span>
                        Verified Specialist
                      </div>
                      {t.isVIP ? (
                        <span style={{
                          display: 'inline-flex',
                          alignItems: 'center',
                          gap: '4px',
                          background: 'linear-gradient(135deg, #7c3aed 0%, #4f46e5 100%)',
                          color: '#ffffff',
                          padding: '3px 10px',
                          borderRadius: '20px',
                          fontSize: '0.72rem',
                          fontWeight: 800,
                          letterSpacing: '0.5px',
                          boxShadow: '0 2px 6px rgba(124, 58, 237, 0.25)'
                        }}>
                          💎 PRO
                        </span>
                      ) : (
                        <span style={{
                          display: 'inline-flex',
                          alignItems: 'center',
                          gap: '4px',
                          background: '#ecfdf5',
                          color: '#065f46',
                          border: '1px solid #a7f3d0',
                          padding: '3px 8px',
                          borderRadius: '20px',
                          fontSize: '0.72rem',
                          fontWeight: 700,
                        }}>
                          🌱 Free Plan
                        </span>
                      )}
                    </div>

                    {/* Card Header (Avatar + Basic Info) */}
                    <div className="card-header-flex">
                      <div className="therapist-avatar-wrap">
                        <img src={t.image} alt={t.name} className="therapist-avatar-img" />
                        <div className="avatar-online-dot" title="Available for appointments"></div>
                      </div>

                      <div className="therapist-meta-header">
                        <h4 className="therapist-name-heading">
                          {t.name}
                        </h4>
                        <div className="therapist-qualification-text" title={t.qualification}>
                          {t.qualification}
                        </div>
                        <span className="therapist-role-badge">
                          {t.specialty}
                        </span>

                        <div className="exp-rating-row">
                          <span className="rating-pill">
                            ★ {t.rating}
                          </span>
                          <span style={{ fontWeight: 600 }}>
                            ⏱️ {t.experience}+ Yrs Exp
                          </span>
                          <span style={{ fontWeight: 700, color: '#059669', background: '#ecfdf5', padding: '2px 8px', borderRadius: '6px', fontSize: '11px', border: '1px solid #a7f3d0' }} title={`30m: ₹${t.durationFees?.[30] || 599} | 50m: ₹${t.durationFees?.[50] || 999} | 80m: ₹${t.durationFees?.[80] || 1499}`}>
                            ₹{t.durationFees?.[30] || Math.round((t.consultationFee || 999) * 0.65)} - ₹{t.durationFees?.[80] || Math.round((t.consultationFee || 999) * 1.5)} (30-80m)
                          </span>
                        </div>
                      </div>
                    </div>

                    {/* Card Body Details */}
                    <div className="therapist-details-section">
                      <div className="info-item-row">
                        <span>📍</span>
                        <span>{t.location}</span>
                      </div>
                      <div className="info-item-row">
                        <span>🗣️</span>
                        <span>Languages: <strong>{t.languages.join(', ')}</strong></span>
                      </div>

                      {/* Concern Tags */}
                      <div className="concerns-tag-wrap">
                        {t.concerns.slice(0, 3).map((concern, i) => (
                          <span key={i} className="concern-tag">
                            {concern}
                          </span>
                        ))}
                        {t.concerns.length > 3 && (
                          <span className="concern-tag" style={{ background: '#f5f3ff', color: '#7c3aed' }}>
                            +{t.concerns.length - 3} more
                          </span>
                        )}
                      </div>

                      {/* Available Slots Indicator */}
                      <div style={{
                        margin: '10px 0',
                        padding: '8px 12px',
                        background: t.availableSlots && t.availableSlots.length > 0 ? '#ecfdf5' : '#f8fafc',
                        borderRadius: '8px',
                        border: t.availableSlots && t.availableSlots.length > 0 ? '1px solid #a7f3d0' : '1px solid #e2e8f0',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        fontSize: '12px'
                      }}>
                        <span style={{
                          fontWeight: 700,
                          color: t.availableSlots && t.availableSlots.length > 0 ? '#065f46' : '#64748b',
                          display: 'flex',
                          alignItems: 'center',
                          gap: '6px'
                        }}>
                          <span>{t.availableSlots && t.availableSlots.length > 0 ? '🟢' : '⚪'}</span>
                          {t.availableSlots && t.availableSlots.length > 0 
                            ? `${t.availableSlots.length} Slot${t.availableSlots.length !== 1 ? 's' : ''} Available` 
                            : 'Open for Bookings'}
                        </span>
                        {t.availableSlots && t.availableSlots.length > 0 && (
                          <span style={{ color: '#047857', fontWeight: 600, fontSize: '11px' }}>
                            Next: {t.availableSlots[0].date}
                          </span>
                        )}
                      </div>
                    </div>

                    {/* Card Actions Footer */}
                    <div className="card-actions-grid">
                      <button
                        type="button"
                        className="btn-view-details"
                        onClick={(e) => handleViewDetails(t, e)}
                      >
                        ℹ️ View Details
                      </button>

                      <button
                        type="button"
                        className="btn-book-session"
                        onClick={(e) => handleBookSession(t, e)}
                        style={{ border: 'none' }}
                      >
                        📅 Book Session
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div style={{ background: '#ffffff', borderRadius: '20px', padding: '60px 20px', textAlign: 'center', border: '1px solid #e2e8f0' }}>
              <div style={{ fontSize: '50px', marginBottom: '16px' }}>🔍</div>
              <h4 style={{ fontWeight: 700, color: '#1e293b', marginBottom: '8px' }}>No specialists match your search</h4>
              <p style={{ color: '#64748b', maxWidth: '450px', margin: '0 auto 20px auto' }}>
                We couldn't find any practitioners matching your selected filters. Try selecting a different category or clearing filters.
              </p>
              <button
                type="button"
                className="thm-btn"
                onClick={() => {
                  setSearchQuery('');
                  setActiveCategory('All');
                  setSelectedConcern('All Concerns');
                  setMembershipFilter('all');
                }}
                style={{ padding: '10px 24px', borderRadius: '10px' }}
              >
                Clear All Filters
              </button>
            </div>
          )}
        </div>
      </section>

      {/* ========================================================================= */}
      {/* AMAHA-STYLE THERAPIST DETAILS MODAL POPUP */}
      {/* ========================================================================= */}
      {selectedTherapist && (
        <div className="therapist-modal-overlay" onClick={() => setSelectedTherapist(null)}>
          <div className="therapist-modal-dialog" onClick={(e) => e.stopPropagation()}>
            <button
              type="button"
              className="modal-close-btn"
              onClick={() => setSelectedTherapist(null)}
              aria-label="Close"
            >
              ✕
            </button>

            {/* Modal Header */}
            <div className="modal-header-hero">
              <img
                src={selectedTherapist.image}
                alt={selectedTherapist.name}
                className="modal-avatar-lg"
              />
              <div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
                  <h3 style={{ fontSize: '1.45rem', fontWeight: 800, color: '#1e1b4b', margin: 0 }}>
                    {selectedTherapist.name}
                  </h3>
                  {selectedTherapist.isVIP ? (
                    <span style={{
                      background: 'linear-gradient(135deg, #7c3aed 0%, #4f46e5 100%)',
                      color: '#ffffff',
                      fontSize: '0.72rem',
                      fontWeight: 800,
                      padding: '2px 8px',
                      borderRadius: '12px'
                    }}>
                      💎 PRO
                    </span>
                  ) : (
                    <span style={{
                      background: '#ecfdf5',
                      color: '#065f46',
                      border: '1px solid #a7f3d0',
                      fontSize: '0.72rem',
                      fontWeight: 700,
                      padding: '2px 8px',
                      borderRadius: '12px'
                    }}>
                      🌱 Free Essential
                    </span>
                  )}
                </div>
                <div style={{ color: '#6d28d9', fontWeight: 700, fontSize: '0.92rem', marginTop: '4px' }}>
                  {selectedTherapist.specialty}
                </div>
                <div style={{ color: '#475569', fontSize: '0.85rem', marginTop: '2px' }}>
                  {selectedTherapist.qualification}
                </div>

                <div style={{ display: 'flex', gap: '14px', alignItems: 'center', marginTop: '10px', fontSize: '0.85rem' }}>
                  <span style={{ background: '#fef9c3', color: '#854d0e', padding: '3px 8px', borderRadius: '6px', fontWeight: 700 }}>
                    ★ {selectedTherapist.rating} ({selectedTherapist.reviewCount} Sessions)
                  </span>
                  <span style={{ fontWeight: 600, color: '#334155' }}>
                    ⏱️ {selectedTherapist.experience}+ Years Practice
                  </span>
                </div>
              </div>
            </div>

            {/* Modal Body */}
            <div className="modal-body-content">
              {/* Overview & Philosophy */}
              <div style={{ marginBottom: '22px' }}>
                <h5 className="modal-section-title">About the Practitioner</h5>
                <p style={{ color: '#4b5563', lineHeight: '1.7', fontSize: '0.92rem', margin: 0 }}>
                  {selectedTherapist.bio}
                </p>
              </div>

              {/* Therapeutic Approach */}
              <div style={{ marginBottom: '22px' }}>
                <h5 className="modal-section-title">Therapeutic Approach & Modalities</h5>
                <p style={{ color: '#4b5563', lineHeight: '1.6', fontSize: '0.92rem', background: '#faf5ff', padding: '14px 18px', borderRadius: '12px', border: '1px solid #f3e8ff', margin: 0 }}>
                  🌿 {selectedTherapist.approach}
                </p>
              </div>

              {/* Areas of Clinical Focus */}
              <div style={{ marginBottom: '22px' }}>
                <h5 className="modal-section-title">Areas of Clinical Focus</h5>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
                  {selectedTherapist.concerns.map((concern, idx) => (
                    <span
                      key={idx}
                      style={{
                        background: '#f1f5f9',
                        color: '#1e293b',
                        padding: '6px 14px',
                        borderRadius: '20px',
                        fontSize: '0.83rem',
                        fontWeight: 600,
                        border: '1px solid #e2e8f0'
                      }}
                    >
                      ✓ {concern}
                    </span>
                  ))}
                </div>
              </div>

              {/* Practical Session Info */}
              <div className="row g-3" style={{ background: '#f8fafc', padding: '16px', borderRadius: '14px', border: '1px solid #e2e8f0' }}>
                <div className="col-sm-6">
                  <div style={{ fontSize: '0.82rem', color: '#64748b' }}>📍 Location:</div>
                  <div style={{ fontSize: '0.88rem', fontWeight: 600, color: '#1e293b' }}>{selectedTherapist.location}</div>
                </div>
                <div className="col-sm-6">
                  <div style={{ fontSize: '0.82rem', color: '#64748b' }}>💰 Session Fee:</div>
                  <div style={{ fontSize: '0.88rem', fontWeight: 700, color: '#059669' }}>₹{selectedTherapist.consultationFee || 999} for {selectedTherapist.consultationDuration || 50} min</div>
                </div>
                <div className="col-sm-6">
                  <div style={{ fontSize: '0.82rem', color: '#64748b' }}>🗣️ Spoken Languages:</div>
                  <div style={{ fontSize: '0.88rem', fontWeight: 600, color: '#1e293b' }}>{selectedTherapist.languages.join(', ')}</div>
                </div>
                <div className="col-sm-6">
                  <div style={{ fontSize: '0.82rem', color: '#64748b' }}>⏱️ Session Duration:</div>
                  <div style={{ fontSize: '0.88rem', fontWeight: 600, color: '#1e293b' }}>{selectedTherapist.consultationDuration || 50} Minutes (1-on-1)</div>
                </div>
              </div>

              {/* Available Date & Time Slots Section */}
              <div style={{ marginTop: '20px' }}>
                <SlotAvailabilityPicker
                  slots={selectedTherapist.availableSlots || []}
                  weeklySchedule={selectedTherapist.weeklySchedule || null}
                  consultationDuration={selectedTherapist.consultationDuration || 50}
                  onSelectSlot={(slot) => handleBookSession(selectedTherapist, null, slot)}
                  title="Check available slots"
                />
              </div>
            </div>

            {/* Modal Footer CTA */}
            <div className="modal-footer-cta">
              <button
                type="button"
                onClick={() => setSelectedTherapist(null)}
                style={{
                  padding: '12px 24px',
                  borderRadius: '12px',
                  border: '1.5px solid #cbd5e1',
                  background: '#ffffff',
                  color: '#475569',
                  fontWeight: 600,
                  fontSize: '0.92rem',
                  cursor: 'pointer'
                }}
              >
                Close
              </button>

              <button
                type="button"
                className="btn-book-session"
                style={{
                  flex: 1,
                  padding: '14px 24px',
                  fontSize: '1rem',
                  borderRadius: '12px',
                  border: 'none'
                }}
                onClick={(e) => handleBookSession(selectedTherapist, e)}
              >
                📅 Book Appointment with {selectedTherapist.rawName} →
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default TherapistsPage;
